import { useState, useEffect } from 'react';
import { API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Car, MapPin, Clock, DollarSign, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const RideShareDialog = ({ barId, barName, trigger }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rideData, setRideData] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      getUserLocation();
    }
  }, [isOpen]);

  const getUserLocation = () => {
    setLoading(true);
    setLocationError(null);

    if (!navigator.geolocation) {
      // Fallback to default Temecula location for demo
      const defaultLocation = { lat: 33.4936, lng: -117.1484 };
      setUserLocation(defaultLocation);
      fetchRideLinks(defaultLocation);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setUserLocation(location);
        await fetchRideLinks(location);
      },
      (error) => {
        // Fallback to default Temecula location for demo/preview environments
        console.log('Geolocation error, using default location:', error);
        const defaultLocation = { lat: 33.4936, lng: -117.1484 };
        setUserLocation(defaultLocation);
        fetchRideLinks(defaultLocation);
      },
      { enableHighAccuracy: true, timeout: 5000 }
    );
  };

  const fetchRideLinks = async (location) => {
    try {
      const response = await axios.get(
        `${API}/rideshare/links/${barId}?user_lat=${location.lat}&user_lng=${location.lng}`
      );
      setRideData(response.data);
    } catch (e) {
      console.error(e);
      toast.error('Failed to get ride information');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRideApp = (service, deeplink) => {
    // Try to open the app, fallback to web
    window.open(deeplink, '_blank');
    toast.success(`Opening ${service}...`);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="glass border border-slate-700 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Car className="w-5 h-5 text-blue-400" />
            Get a Ride to {barName}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="w-10 h-10 text-blue-400 animate-spin mx-auto mb-4" />
            <p className="text-slate-400">Getting your location...</p>
          </div>
        ) : locationError ? (
          <div className="text-center py-8">
            <MapPin className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-slate-300 mb-4">{locationError}</p>
            <Button onClick={getUserLocation} variant="outline">
              Try Again
            </Button>
          </div>
        ) : rideData ? (
          <div className="space-y-4">
            {/* Trip Info */}
            <Card className="bg-slate-800/50 p-4 border border-slate-700">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-purple-400" />
                  <span className="text-white font-medium">{rideData.bar_name}</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 bg-slate-700/50 rounded">
                  <MapPin className="w-4 h-4 text-blue-400 mx-auto mb-1" />
                  <p className="text-lg font-bold text-white">{rideData.distance_miles}</p>
                  <p className="text-xs text-slate-400">miles</p>
                </div>
                <div className="p-2 bg-slate-700/50 rounded">
                  <Clock className="w-4 h-4 text-green-400 mx-auto mb-1" />
                  <p className="text-lg font-bold text-white">{rideData.estimated_duration_min}</p>
                  <p className="text-xs text-slate-400">min</p>
                </div>
                <div className="p-2 bg-slate-700/50 rounded">
                  <DollarSign className="w-4 h-4 text-yellow-400 mx-auto mb-1" />
                  <p className="text-lg font-bold text-white">${rideData.estimated_fare.low}-{rideData.estimated_fare.high}</p>
                  <p className="text-xs text-slate-400">est.</p>
                </div>
              </div>
            </Card>

            {/* Uber Button */}
            <Button
              onClick={() => handleOpenRideApp('Uber', rideData.uber.deeplink)}
              className="w-full bg-black hover:bg-gray-900 text-white h-14"
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white rounded flex items-center justify-center">
                    <span className="text-black font-bold text-sm">U</span>
                  </div>
                  <div className="text-left">
                    <p className="font-semibold">Uber</p>
                    <p className="text-xs text-gray-400">Open in app</p>
                  </div>
                </div>
                <ExternalLink className="w-5 h-5" />
              </div>
            </Button>

            {/* Lyft Button */}
            <Button
              onClick={() => handleOpenRideApp('Lyft', rideData.lyft.deeplink)}
              className="w-full bg-[#FF00BF] hover:bg-[#E600AC] text-white h-14"
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white rounded flex items-center justify-center">
                    <span className="text-[#FF00BF] font-bold text-sm">L</span>
                  </div>
                  <div className="text-left">
                    <p className="font-semibold">Lyft</p>
                    <p className="text-xs text-pink-200">Open in app</p>
                  </div>
                </div>
                <ExternalLink className="w-5 h-5" />
              </div>
            </Button>

            <p className="text-xs text-slate-500 text-center">
              Fare estimates are approximate. Actual fare may vary based on traffic and route.
            </p>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default RideShareDialog;
